<template>
  <v-dialog
    v-model="openDialogProxy"
    persistent
    max-width="900px"
  >
    <v-card class="premium-card" :class="{ 'theme--dark': isDark }">
      <!-- Header -->
      <v-toolbar flat class="premium-toolbar" color="primary" dark>
        <div class="d-flex align-center">
          <v-avatar size="36" class="mr-3 mb-4 premium-avatar">
            <v-icon>mdi-ticket-confirmation</v-icon>
          </v-avatar>
          <div class="d-flex flex-column mb-4">
            <v-toolbar-title class="premium-title">Novo ticket</v-toolbar-title>
            <span class="premium-subtitle">Preenche os dados e envia para suporte</span>
          </div>
        </div>

        <v-spacer />

        <v-btn icon @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="premium-body">
        <!-- Sucesso -->
        <v-fade-transition>
          <div v-if="success" class="premium-success">
            <v-icon size="52" color="success" class="mb-3">mdi-check-circle</v-icon>
            <h3 class="mb-2">Ticket enviado com sucesso!</h3>
            <p class="mb-0">A nossa equipa vai analisar o teu pedido.</p>
          </div>
        </v-fade-transition>

        <!-- Form -->
        <validation-observer ref="form_ticket" v-slot="{ invalid }">
          <v-form v-if="!success" class="pt-6" @submit.prevent="addTicket(invalid)">
            <v-row>
              <v-col cols="12">
                <validation-provider v-slot="{ errors }" vid="title" :name="'título'" rules="required">
                  <v-text-field
                    v-model="ticket.title"
                    :error-messages="errors"
                    label="Título *"
                    outlined
                    dense
                    class="premium-input"
                    prepend-inner-icon="mdi-format-title"
                    :disabled="loading"
                  />
                </validation-provider>
              </v-col>

              <v-col cols="12">
                <validation-provider v-slot="{ errors }" vid="description" :name="'descrição'" rules="required">
                  <v-textarea
                    v-model="ticket.description"
                    :error-messages="errors"
                    label="Descrição *"
                    outlined
                    auto-grow
                    rows="4"
                    class="premium-input"
                    prepend-inner-icon="mdi-text"
                    :disabled="loading"
                  />
                </validation-provider>
              </v-col>

              <v-col cols="12">
                <v-file-input
                  v-model="ticket.files"
                  multiple
                  label="Anexos"
                  outlined
                  dense
                  class="premium-input"
                  :disabled="loading"
                  show-size
                  chips
                />
              </v-col>
            </v-row>

            <!-- Footer actions -->
            <div class="premium-footer">
              <v-btn
                text
                @click="close"
                :disabled="loading"
              >
                Cancelar
              </v-btn>

              <v-spacer />

              <v-btn
                color="primary"
                class="premium-cta"
                depressed
                type="submit"
                :loading="loading"
                :disabled="invalid || submitted"
              >
                <v-icon left>mdi-content-save</v-icon>
                Guardar
              </v-btn>
            </div>
          </v-form>
        </validation-observer>
      </v-card-text>

      <!-- Só para dar opção de fechar no sucesso -->
      <v-card-actions v-if="success" class="px-6 pb-6">
        <v-spacer />
        <v-btn color="primary" class="premium-cta" @click="$emit('close-success')">
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    openDialog: Boolean,
  },
  data() {
    return {
      loading: false,
      submitted: false, // <- desativa imediatamente ao enviar
      success: false,
      ticket: {
        title: "",
        description: "",
        files: [],
      },
      error: {
        title: "",
        message: "",
      },
    };
  },
  computed: {
    isDark() {
      // mantendo compatibilidade com o teu $root.$vuetify
      return !!this.$root?.$vuetify?.theme?.isDark;
    },

    // proxy para conseguir fechar pelo botão e emitir close
    openDialogProxy: {
      get() {
        return this.openDialog;
      },
      set(val) {
        if (!val) this.$emit("close");
      },
    },
  },
  watch: {
    // sempre que abrir a modal de novo, reset total (inclui reativar botão)
    openDialog(val) {
      if (val) this.resetState();
    },
  },
  methods: {
    resetState() {
      this.loading = false;
      this.submitted = false;
      this.success = false;
      this.error = { title: "", message: "" };
      this.ticket = { title: "", description: "", files: [] };

      // limpa erros do vee-validate se existir
      this.$nextTick(() => {
        try {
          this.$refs.form_ticket?.reset();
        } catch (e) {}
      });
    },

    close() {
      this.$emit("close");
    },

    async addTicket(invalid) {
      if (invalid) return;

      // desativa IMEDIATAMENTE e mantém desativado até fechar/reabrir
      this.submitted = true;
      this.loading = true;

      try {
        const formData = new FormData();

        const files = this.ticket.files || [];
        for (let i = 0; i < files.length; i++) {
          formData.append("files[]", files[i]);
        }

        formData.append("title", this.ticket.title);
        formData.append("description", this.ticket.description);
        formData.append("total_files", String(files.length));

        await this.$store.dispatch("createTicket", formData);

        // mostra sucesso
        this.success = true;
      } catch (err) {
        // mantém o botão desativado (como pediste)
        this.loading = false;

        if (err?.response?.status === 422) {
          // ajusta para o teu observer (estavas a usar refs diferentes)
          try {
            this.$refs.form_ticket?.setErrors(err.response.data.errors);
          } catch (e) {}
          return;
        }

        this.error.title = "Erro: " + (err?.response?.status ?? "");
        this.error.message = err?.response?.data?.message ?? "Erro ao enviar ticket.";
      } finally {
        // loading pode parar para não ficar spinner infinito
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.premium-card{
  border-radius: 18px;
  overflow: hidden;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(17, 27, 45, 0.08);
  box-shadow: 0 24px 80px rgba(17, 27, 45, 0.18);
}

.theme--dark.premium-card{
  background: rgba(30, 41, 59, 0.92);
  border-color: rgba(255,255,255,0.10);
}

.premium-toolbar{
  padding: 14px 18px;
}

.premium-avatar{
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.25);
}

.premium-title{
  font-weight: 800;
  letter-spacing: -0.02em;
}

.premium-subtitle{
  font-size: 12px;
  opacity: 0.9;
  margin-top: -2px;
}

.premium-body{
  padding: 22px 24px 10px 24px;
}

.premium-input :deep(.v-input__control){
  border-radius: 14px;
}

.premium-input :deep(.v-field){
  border-radius: 14px;
}

.premium-footer{
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid rgba(17, 27, 45, 0.08);
}

.theme--dark .premium-footer{
  border-top-color: rgba(255,255,255,0.10);
}

.premium-cta{
  border-radius: 14px !important;
  font-weight: 700;
  text-transform: none;
  padding: 0 18px;
}

.premium-success{
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 10px 10px 10px;
}

.premium-success h3{
  font-weight: 800;
  letter-spacing: -0.02em;
}
</style>
